import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { Module } from 'src/app/core/interfaces/module.interface';
import { Page } from 'src/app/core/interfaces/page.interface';
import { environment } from 'src/environments/environment';
import { Filter } from '../interfaces/filter.interface';

@Injectable({
  providedIn: 'root',
})
export class ModulesService {
  private baseUrl = environment.adminApiBaseUrl;
  private moduleApiUrl = `${this.baseUrl}/Module`;

  private pageApiUrl = `${this.baseUrl}/Page`;

  constructor(private http: HttpClient, private router: Router) { }

  getModuleById = (id: number): Observable<any> => {
    return this.http.get(this.moduleApiUrl + '/' + id);
  }

  private lookupBaseUrl = environment.apiBaseUrl;

  /**
   * Lookup/Modules — الاستجابة: data[] فيه id, nameAr, nameEn, meta: { pages: [{ id, titleEn, titleAr, path }] }
   * نحوّلها إلى شكل module مع pages للواجهة (create-update-package).
   */
  listModules = (PageNumber = 1, PageSize = 10, Term = '', filters?: Filter[], orderBy?: string): Observable<any> => {
    let orderByValue: string[] = [];
    if (orderBy) {
      if (orderBy.endsWith(' asc')) {
        orderByValue = [orderBy.replace(' asc', '')];
      } else {
        orderByValue = [orderBy];
      }
    }
    const params = {
      term: Term,
      advancedFilter: filters ?? [],
      pageNumber: PageNumber,
      pageSize: PageSize,
      orderBy: orderByValue,
    };
    return this.http.post(`${this.lookupBaseUrl}/Lookup/Modules`, params).pipe(
      map((res: { data?: unknown[] }) => {
        if (!res || !Array.isArray(res.data)) return res;
        return {
          ...res,
          data: res.data.map((item: unknown) => this.mapLookupModuleToModule(item)),
        };
      })
    );
  }

  /**
   * تحويل عنصر من استجابة Lookup/Modules (id, nameAr, nameEn, meta.pages[].actions) إلى شكل module للواجهة.
   * الـ API يعيد actions داخل كل page، والواجهة تتوقع actionList.
   */
  private mapLookupModuleToModule(item: unknown): { id: number; nameAr: string; nameEn: string; pages: Array<{ id: number; titleAr: string; titleEn: string; path?: string; actionList: Array<{ code: string; name: string; permissionKey: string }> }> } {
    const i = item as {
      id: number;
      nameAr?: string;
      nameEn?: string;
      meta?: {
        pages?: Array<{
          id: number;
          titleAr?: string;
          titleEn?: string;
          path?: string;
          actions?: Array<{ code: string; name: string; permissionKey: string }>;
        }>;
      };
    };
    const nameAr = i.nameAr ?? i.nameEn ?? '';
    const nameEn = i.nameEn ?? i.nameAr ?? '';
    const rawPages = i.meta?.pages ?? [];
    const pages = rawPages.map((p) => ({
      id: p.id,
      titleAr: p.titleAr ?? p.titleEn ?? '',
      titleEn: p.titleEn ?? p.titleAr ?? '',
      path: p.path ?? '',
      actionList: p.actions ?? [],
    }));
    return { id: i.id, nameAr, nameEn, pages };
  }

  /**
   * Lookup الأنظمة لصفحة create-update-page فقط. POST /api/v1/Lookup/Modules
   */
  listModulesLookupForCreatePage = (PageNumber = 1, PageSize = 10, Term = '', filters?: Filter[], orderBy?: string): Observable<any> => {
    let orderByValue: string[] = [];
    if (orderBy) {
      if (orderBy.endsWith(' asc')) {
        orderByValue = [orderBy.replace(' asc', '')];
      } else {
        orderByValue = [orderBy];
      }
    }
    const params = {
      term: Term,
      advancedFilter: filters ?? [],
      pageNumber: PageNumber,
      pageSize: PageSize,
      orderBy: orderByValue,
    };
    return this.http.post(`${this.lookupBaseUrl}/Lookup/Modules`, params).pipe(
      map((res: { data?: unknown[] }) => {
        if (!res || !Array.isArray(res.data)) return res;
        return {
          ...res,
          data: res.data.map((item: unknown) => {
            const i = item as Record<string, unknown> & { nameAr?: string; nameEn?: string };
            const nameAr = i.nameAr ?? i.nameEn ?? '';
            const nameEn = i.nameEn ?? i.nameAr ?? '';
            return { ...i, nameAr, nameEn };
          }),
        };
      })
    );
  };

  /**
   * قائمة الأنظمة لصفحة list-modules فقط. يستدعي POST /admin/api/v1/Module/List
   * لا يستخدم في dropdownات أو فلاتر أو صفحات أخرى.
   */
  listModulesForSystemsPage = (PageNumber = 1, PageSize = 10, Term = '', filters?: Filter[], orderBy?: string): Observable<any> => {
    let orderByValue: string[] = [];
    if (orderBy) {
      if (orderBy.endsWith(' asc')) {
        orderByValue = [orderBy.replace(' asc', '')];
      } else {
        orderByValue = [orderBy];
      }
    }
    const params = {
      term: Term,
      advancedFilter: filters,
      pageNumber: PageNumber,
      pageSize: PageSize,
      orderBy: orderByValue,
    };
    return this.http.post(`${this.moduleApiUrl}/List`, params);
  }

  myModules = (): Observable<any> => {
    return this.http.get(`${this.moduleApiUrl}/my-modules`);
  }

  createModule(data: Module): Observable<any> {
    return this.http.post(this.moduleApiUrl, data);
  }

  updateModule(data: Module): Observable<any> {
    return this.http.put(this.moduleApiUrl, data);
  }

  deleteModule(id: number): Observable<any> {
    return this.http.delete(this.moduleApiUrl + '/' + id);
  }

  getPage(id: number): Observable<any> {
    return this.http.get(this.pageApiUrl + '/' + id);
  }

  getAllPage(): Observable<any> {
    return this.http.get(this.pageApiUrl);
  }

  listPages = (PageNumber = 1, PageSize = 10, Term = '', filters?: Filter[], orderBy?: string): Observable<any> => {
    let orderByValue: string[] = [];
    if (orderBy) {
      // إذا كان تصاعدي (asc) - لا نرسل asc لأنه default
      if (orderBy.endsWith(' asc')) {
        orderByValue = [orderBy.replace(' asc', '')];
      } else {
        // إذا كان تنازلي (desc) - نرسل مع desc
        orderByValue = [orderBy];
      }
    }
    
    const params = {
      PageNumber,
      PageSize,
      Term,
      advancedFilter: filters,
      orderBy: orderByValue,
    };
    return this.http.post(`${this.pageApiUrl}/List`, params);
  }

  createPage(data: Page): Observable<any> {
    return this.http.post(this.pageApiUrl, data);
  }

  updatePage(data: Page): Observable<any> {
    return this.http.put(this.pageApiUrl, data);
  }

  deletePage(id: number): Observable<any> {
    return this.http.delete(this.pageApiUrl + '/' + id);
  }

  getPageActions(): Observable<any> {
    return this.http.get(this.pageApiUrl + '/actions');
  }
}
